import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Mail, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitingListPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/waiting-list`, formData);
      toast.success("You're on the list!");
      navigate("/thank-you");
    } catch (error) {
      toast.error("Failed to join waiting list");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-12 min-h-[80vh] flex items-center">
      <div className="max-w-lg mx-auto px-4 sm:px-6 w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Be the first to know
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Join the Waiting List</h1>
          <p className="text-muted-foreground">
            Get early access to new validation tools, frameworks, and founder resources.
          </p>
        </div>

        <Card data-testid="waiting-list-card">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  required
                  className="mt-1"
                  data-testid="waitlist-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                  className="mt-1"
                  data-testid="waitlist-email-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full rounded-full h-12" 
                disabled={submitting}
                data-testid="waitlist-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Join the Waiting List
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-border/40">
              <p className="text-sm text-muted-foreground text-center mb-4">
                What you'll get:
              </p>
              <ul className="space-y-3">
                {[
                  "Early access to new validation tools",
                  "Weekly founder insights & tips",
                  "Exclusive frameworks & templates"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Ready to test your idea now?{" "}
          <Link to="/quiz" className="text-primary hover:underline" data-testid="waitlist-quiz-link">
            Take the Stress Test
          </Link>
        </p>
      </div>
    </div>
  );
}
